import React, { useEffect, useState } from "react";
import { FaTrash, FaSync, FaSave, FaTimes, FaCalendarAlt } from "react-icons/fa";
import PageHeader from "../../core/common/header/PageHeader";
import { Calendar, momentLocalizer } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const localizer = momentLocalizer(moment);

// ✅ Create DnD Calendar
const DnDCalendar = withDragAndDrop(Calendar);

interface CalendarEvent {
  id: number;
  title: string;
  start: Date;
  end: Date;
  category: string;
  description?: string;
  location?: string;
  color?: string;
  createdByName: string;
}

interface EventFormData {
  title: string;
  startTime: string;
  endTime: string;
  category: string;
  description: string;
  location: string;
  color: string;
}

const AdminCalendarManagement = () => {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(
    null
  );
  const [isEditMode, setIsEditMode] = useState(false);

  const [formData, setFormData] = useState<EventFormData>({
    title: "",
    startTime: "",
    endTime: "",
    category: "Meeting",
    description: "",
    location: "",
    color: "#009fe3",
  });

  // ✅ Theme-based color palette matching Guideline2Germany brand
  const categories = [
    { value: "Meeting", label: "Meeting", color: "#009fe3" },
    { value: "Project", label: "Project", color: "#0088cc" },
    { value: "Client", label: "Client", color: "#0066a1" },
    { value: "Training", label: "Training", color: "#33b5f5" },
    { value: "Development", label: "Development", color: "#0077b3" },
    { value: "Other", label: "Other", color: "#005580" },
  ];

  // ✅ Helper function to format datetime for backend (removes timezone)
  const formatDateTimeForBackend = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  };

  const fetchEvents = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(`${API_BASE}/api/admin/events`, {
        credentials: "include",
      });

      if (!response.ok) throw new Error("Failed to fetch events");

      const data = await response.json();

      // Transform API data to calendar format
      const transformedEvents = data.map((event: any) => ({
        id: event.id,
        title: event.title,
        start: new Date(event.startTime),
        end: new Date(event.endTime),
        category: event.category,
        description: event.description,
        location: event.location,
        color: event.color,
        createdByName: event.createdByName,
      }));

      setEvents(transformedEvents);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleSelectSlot = ({ start, end }: { start: Date; end: Date }) => {
    setIsEditMode(false);
    setSelectedEvent(null);
    setFormData({
      title: "",
      startTime: moment(start).format("YYYY-MM-DDTHH:mm"),
      endTime: moment(end).format("YYYY-MM-DDTHH:mm"),
      category: "Meeting",
      description: "",
      location: "",
      color: "#009fe3",
    });
    setShowEventModal(true);
  };

  const handleSelectEvent = (event: object, e: React.SyntheticEvent) => {
    const calendarEvent = event as CalendarEvent;
    setIsEditMode(true);
    setSelectedEvent(calendarEvent);
    setFormData({
      title: calendarEvent.title,
      startTime: moment(calendarEvent.start).format("YYYY-MM-DDTHH:mm"),
      endTime: moment(calendarEvent.end).format("YYYY-MM-DDTHH:mm"),
      category: calendarEvent.category,
      description: calendarEvent.description || "",
      location: calendarEvent.location || "",
      color: calendarEvent.color || "#009fe3",
    });
    setShowEventModal(true);
  };

  const handleEventDrop = async (args: any) => {
    const { event, start, end } = args;

    try {
      const eventData = {
        title: event.title,
        startTime: formatDateTimeForBackend(start),
        endTime: formatDateTimeForBackend(end),
        category: event.category,
        description: event.description,
        location: event.location,
        color: event.color,
      };

      const response = await fetch(`${API_BASE}/api/admin/events/${event.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(eventData),
      });

      if (!response.ok) throw new Error("Failed to update event");

      fetchEvents();
    } catch (err) {
      alert(
        "Failed to reschedule event: " +
          (err instanceof Error ? err.message : "Unknown error")
      );
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const eventData = {
        title: formData.title,
        startTime: formatDateTimeForBackend(new Date(formData.startTime)),
        endTime: formatDateTimeForBackend(new Date(formData.endTime)),
        category: formData.category,
        description: formData.description,
        location: formData.location,
        color: formData.color,
      };

      const url = isEditMode
        ? `${API_BASE}/api/admin/events/${selectedEvent?.id}`
        : `${API_BASE}/api/admin/events`;

      const method = isEditMode ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(eventData),
      });

      if (!response.ok) throw new Error("Failed to save event");

      alert(
        isEditMode
          ? "Event updated successfully!"
          : "Event created successfully!"
      );
      setShowEventModal(false);
      fetchEvents();
    } catch (err) {
      alert(
        "Failed to save event: " +
          (err instanceof Error ? err.message : "Unknown error")
      );
    }
  };

  const handleDelete = async () => {
    if (!selectedEvent) return;

    if (!window.confirm("Are you sure you want to delete this event?")) {
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE}/api/admin/events/${selectedEvent.id}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      if (!response.ok) throw new Error("Failed to delete event");

      alert("Event deleted successfully!");
      setShowEventModal(false);
      fetchEvents();
    } catch (err) {
      alert(
        "Failed to delete event: " +
          (err instanceof Error ? err.message : "Unknown error")
      );
    }
  };

  const eventStyleGetter = (event: any) => {
    return {
      style: {
        backgroundColor: event.color || "#009fe3",
        borderRadius: "5px",
        opacity: 0.9,
        color: "white",
        border: "0px",
        display: "block",
      },
    };
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaCalendarAlt />}
        title="Calendar Management"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Calendar" },
        ]}
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Instructions */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border-t-4 border-[#009fe3]">
          <h3 className="font-bold text-lg mb-3 text-gray-800">
            📅 How to Use Calendar
          </h3>
          <div className="grid md:grid-cols-3 gap-4 text-sm text-gray-600">
            <div className="flex items-start gap-2">
              <span className="text-[#009fe3]">🖱️</span>
              <span>
                <strong>Click empty slot</strong> to create new event
              </span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-[#009fe3]">👆</span>
              <span>
                <strong>Click event</strong> to view/edit details
              </span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-[#009fe3]">🎯</span>
              <span>
                <strong>Drag & drop</strong> to reschedule events
              </span>
            </div>
          </div>

          {/* Category Legend */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <h4 className="font-semibold text-sm text-gray-700 mb-2">
              Event Categories:
            </h4>
            <div className="flex flex-wrap gap-3">
              {categories.map((cat) => (
                <div key={cat.value} className="flex items-center gap-2">
                  <div
                    className="w-4 h-4 rounded"
                    style={{ backgroundColor: cat.color }}
                  ></div>
                  <span className="text-sm text-gray-600">{cat.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Calendar */}
        {loading ? (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <FaSync className="animate-spin h-12 w-12 text-[#009fe3] mx-auto mb-4" />
            <p className="text-gray-600">Loading calendar...</p>
          </div>
        ) : error ? (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded">
              <p className="font-semibold">Error</p>
              <p>{error}</p>
            </div>
            <button
              onClick={fetchEvents}
              className="w-full bg-[#009fe3] hover:bg-[#007cb5] text-white py-3 rounded-lg font-semibold transition-colors"
            >
              Retry
            </button>
          </div>
        ) : (
          <div
            className="bg-white rounded-xl shadow-lg p-6 border-t-4 border-[#009fe3]"
            style={{ height: "700px" }}
          >
            <DnDCalendar
              localizer={localizer}
              events={events}
              startAccessor={(event: object) => (event as CalendarEvent).start}
              endAccessor={(event: object) => (event as CalendarEvent).end}
              style={{ height: "100%" }}
              selectable
              onSelectSlot={handleSelectSlot}
              onSelectEvent={handleSelectEvent}
              onEventDrop={handleEventDrop}
              eventPropGetter={eventStyleGetter}
              views={["month", "week", "day", "agenda"]}
              defaultView="agenda"
              step={30}
              showMultiDayTimes
              draggableAccessor={() => true}
              resizable
              length={730}
            />
          </div>
        )}

        {/* Event Modal */}
        {showEventModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto border-t-4 border-[#009fe3]">
              <h2 className="text-2xl font-bold mb-6 text-gray-800">
                {isEditMode ? "Edit Event" : "Create New Event"}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Title */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Event Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009fe3] focus:border-[#009fe3]"
                    placeholder="Team Meeting"
                  />
                </div>

                {/* Start Time */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Start Time <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.startTime}
                    onChange={(e) =>
                      setFormData({ ...formData, startTime: e.target.value })
                    }
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009fe3] focus:border-[#009fe3]"
                  />
                </div>

                {/* End Time */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    End Time <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.endTime}
                    onChange={(e) =>
                      setFormData({ ...formData, endTime: e.target.value })
                    }
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009fe3] focus:border-[#009fe3]"
                  />
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => {
                      const cat = categories.find(
                        (c) => c.value === e.target.value
                      );
                      setFormData({
                        ...formData,
                        category: e.target.value,
                        color: cat?.color || "#009fe3",
                      });
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009fe3] focus:border-[#009fe3]"
                  >
                    {categories.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Location */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Location
                  </label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) =>
                      setFormData({ ...formData, location: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009fe3] focus:border-[#009fe3]"
                    placeholder="Conference Room A"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009fe3] focus:border-[#009fe3]"
                    placeholder="Event details..."
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 bg-[#009fe3] hover:bg-[#0088cc] text-white py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                  >
                    <FaSave />
                    {isEditMode ? "Update Event" : "Create Event"}
                  </button>
                  {isEditMode && (
                    <button
                      type="button"
                      onClick={handleDelete}
                      className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2"
                    >
                      <FaTrash />
                      Delete
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowEventModal(false)}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2"
                  >
                    <FaTimes />
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>

      {/* Custom CSS for Calendar - Theme Colors with READABLE Agenda */}
      <style>{`
        .rbc-calendar {
          font-size: 14px;
        }
        
        .rbc-event {
          padding: 2px 5px;
          font-size: 12px;
        }
        
        /* Toolbar - Brand Colors */
        .rbc-toolbar button {
          color: #374151;
          border: 1px solid #d1d5db;
          padding: 8px 16px;
          background: white;
          font-weight: 500;
          border-radius: 5px;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .rbc-toolbar button:hover {
          background: #e0f7ff;
          border-color: #009fe3;
          color: #009fe3;
        }
        
        .rbc-toolbar button.rbc-active {
          background: linear-gradient(135deg, #009fe3 0%, #0088cc 100%);
          color: white;
          border-color: #009fe3;
        }
        
        /* Today - Brand Color */
        .rbc-today {
          background-color: #e0f7ff;
        }
        
        /* Header - Brand Colors */
        .rbc-header {
          padding: 12px 3px;
          font-weight: 600;
          color: #009fe3;
          border-bottom: 2px solid #009fe3;
        }
        
        .rbc-event-label {
          font-size: 80%;
        }
        
        /* Show More - Brand Colors */
        .rbc-show-more {
          background-color: #e0f7ff;
          color: #009fe3;
          font-weight: 600;
          padding: 2px 5px;
          border-radius: 3px;
        }
        
        .rbc-show-more:hover {
          background-color: #b3e5fc;
          color: #0088cc;
        }

        /* Selected Date - Brand Colors */
        .rbc-day-slot .rbc-time-slot.rbc-now {
          background-color: #e0f7ff;
        }

        /* Drag and Drop Styles */
        .rbc-addons-dnd .rbc-addons-dnd-resizable {
          position: relative;
        }
        
        .rbc-addons-dnd .rbc-event {
          cursor: move;
        }
        
        .rbc-addons-dnd-dragging {
          opacity: 0.5;
        }

        /* ✅ FIXED: Agenda View - READABLE Colors */
        .rbc-agenda-view {
          font-size: 14px;
        }

        .rbc-agenda-table {
          border-collapse: collapse;
        }

        /* ✅ Date and Time cells - DARK TEXT for readability */
        .rbc-agenda-date-cell,
        .rbc-agenda-time-cell {
          padding: 12px;
          font-weight: 600;
          color: #1f2937 !important;  /* Dark gray - readable */
          background-color: #f9fafb;  /* Light gray background */
        }

        /* ✅ Event cell - DARK TEXT */
        .rbc-agenda-event-cell {
          padding: 12px;
          color: #374151 !important;  /* Dark gray text */
          font-weight: 500;
        }

        .rbc-agenda-view table.rbc-agenda-table tbody > tr > td + td {
          padding: 12px;
        }

        .rbc-agenda-view table.rbc-agenda-table tbody > tr + tr {
          border-top: 1px solid #e5e7eb;
        }

        /* ✅ Row hover - Light blue background */
        .rbc-agenda-view table.rbc-agenda-table tbody > tr:hover {
          background-color: #e0f7ff;
        }

        /* ✅ Agenda Header - White text on blue gradient (high contrast) */
        .rbc-agenda-view table.rbc-agenda-table thead > tr > th {
          padding: 12px;
          background: linear-gradient(135deg, #009fe3 0%, #0088cc 100%);
          color: white !important;
          font-weight: 600;
          text-align: left;
        }

        /* Time Slots */
        .rbc-time-slot {
          border-top: 1px solid #f0f0f0;
        }

        .rbc-current-time-indicator {
          background-color: #009fe3;
          height: 2px;
        }

        /* Selection */
        .rbc-slot-selection {
          background-color: rgba(0, 159, 227, 0.3);
        }

        /* Off Range */
        .rbc-off-range {
          color: #d1d5db;
        }

        .rbc-off-range-bg {
          background: #fafafa;
        }

        /* Month View Date Numbers */
        .rbc-date-cell {
          padding: 4px;
        }

        .rbc-date-cell > a {
          color: #374151;
          font-weight: 500;
        }

        .rbc-date-cell.rbc-now > a {
          color: #009fe3;
          font-weight: 700;
        }
      `}</style>
    </div>
  );
};

export default AdminCalendarManagement;
